import { EDLINK_FIREBASE_PROVIDER_ID, readEdlinkInitiationParams, stripEdlinkInitiationParams } from '@airreading/auth';
import {
  User as FirebaseUser,
  GoogleAuthProvider,
  OAuthProvider,
  browserLocalPersistence,
  onIdTokenChanged,
  signInWithPopup,
  signInWithRedirect,
  signOut,
} from 'firebase/auth';
import { useCallback, useEffect, useRef, useState } from 'react';

import { getEnvironmentLevel } from '@/lib/common-utils';
import { logError } from '@/lib/error-logger';
import { auth } from './firebase';

const googleOauthProvider = new GoogleAuthProvider();
googleOauthProvider.setCustomParameters({
  prompt: 'select_account',
});

export function useFirebaseAuth() {
  const [identityProviderUser, setIdentityProviderUser] = useState<FirebaseUser | null | undefined>(undefined);

  // localhost uses popup since redirect auth is not reliable without
  // a same-site authDomain (/__/auth/* proxy)
  const shouldUseRedirect = getEnvironmentLevel() !== 'local';
  const edlinkInitiationStarted = useRef(false);

  useEffect(() => {
    const unsubscribe = onIdTokenChanged(auth, (user) => {
      sessionStorage.removeItem('auth:redirectInProgress');
      setIdentityProviderUser(user ?? null);
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (sessionStorage.getItem('auth:redirectInProgress') === '1') {
      return;
    }

    const edlinkClientId = import.meta.env.VITE_EDLINK_CLIENT_ID;

    if (!edlinkClientId) {
      logError(new Error('VITE_EDLINK_CLIENT_ID is not configured'), {
        component: 'useFirebaseAuth',
        action: 'signInWithEdlink',
      });
      return;
    }

    const edlinkInitiationParams = readEdlinkInitiationParams(new URL(window.location.href), edlinkClientId);

    if (!edlinkInitiationParams || edlinkInitiationStarted.current || Boolean(identityProviderUser)) {
      return;
    }

    edlinkInitiationStarted.current = true;

    const cleanUrl = new URL(stripEdlinkInitiationParams(window.location.href));
    // Clear any stale redirect from a previous user's session so the incoming
    // student lands on their own home page after SSO completes.
    cleanUrl.searchParams.delete('redirect');
    history.replaceState(null, '', cleanUrl.toString());

    const provider = new OAuthProvider(EDLINK_FIREBASE_PROVIDER_ID);
    provider.setCustomParameters({ login_hint: edlinkInitiationParams.loginHint });

    (async () => {
      try {
        await auth.setPersistence(browserLocalPersistence);
        const signInMethod = shouldUseRedirect ? signInWithRedirect : signInWithPopup;

        if (shouldUseRedirect) {
          sessionStorage.setItem('auth:redirectInProgress', '1');
        }

        await signInMethod(auth, provider);
      } catch (error: unknown) {
        edlinkInitiationStarted.current = false;
        sessionStorage.removeItem('auth:redirectInProgress');
        logError(error instanceof Error ? error : new Error('Edlink OIDC sign-in failed', { cause: error }), {
          component: 'useFirebaseAuth',
          action: 'signInWithEdlink',
        });
      }
    })();
  }, [shouldUseRedirect, identityProviderUser]);

  const login = useCallback(async () => {
    await auth.setPersistence(browserLocalPersistence);

    if (shouldUseRedirect) {
      sessionStorage.setItem('auth:redirectInProgress', '1');
    }

    const signInMethod = shouldUseRedirect ? signInWithRedirect : signInWithPopup;

    return signInMethod(auth, googleOauthProvider).catch((error) => {
      sessionStorage.removeItem('auth:redirectInProgress');
      console.log(`${error.code}: ${error.message}`);
    });
  }, [shouldUseRedirect]);

  const logout = useCallback(async () => {
    return signOut(auth);
  }, []);

  return { identityProviderUser, login, logout };
}
